import { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { isEqual } from 'lodash';
import Yup from '../../utils/yup';

import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import useMenus from '../../context/MenuContext';
import useUsers from '../../context/UsersContext';
import { FormButtonPanelUI, FormHeaderUI, FormBase, DeleteDialogUI } from '../Interface';
import { cleanValues, generateAutocompleteItems } from '../../utils/helper';
import bankAccountService from '../../service/bankAccount.service';
import BankAccountReports from './BankAccountReports';
import BankAccountFields from './BankAccountFields';
import masterDataService from '../../service/masterData.service';
import BankAccountTabs from './BankAccountTabs';

export const BankAccountPage = () => {
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const { getUser, user } = useUsers();
  const [refetchTable, setRefetchTable] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [canEdit, setCanEdit] = useState(true);

  const [defaultValues, setDefaultValues] = useState({});
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [initValues, setInitValues] = useState({});
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [autoCompleteOptions, setAutoCompleteOptions] = useState({
    currency: [],
    costCenter: [],
  });
  const [autoCompleteData, setAutoCompleteData] = useState({});

  const def = {
    name: '',
    bankAccountNumber: '',
    iban: '',
    currencyCode: '',
    costCenterCode: '',
    description: '',
  };

  useEffect(() => {
    getMenus();
    if (!Object.keys(user).length) getUser();

    setDefaultValues(def);
    setDefaultSelectedValues(def);
    initDefaultValues();
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('bankAccount');
    setCanEdit(getCanEdit(submenuId));
  }, [menus]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultSelectedValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultValues);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [selectedValues]);

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .costCenterToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .currencyToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          costCenter: resp[0],
          currency: resp[1],
        };

        setAutoCompleteData(aData);

        const filteredCurrency = aData.currency.filter((c) => c.active);
        const filteredCostCenter = aData.costCenter.filter((cc) => !cc.deletedBy);

        const acOptions = {
          costCenter: generateAutocompleteItems(filteredCostCenter, 'name', 'code'),
          currency: generateAutocompleteItems(filteredCurrency, ['code', 'name'], 'code'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {});
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(),
    bankAccountNumber: Yup.string().required(),
    iban: Yup.string().nullable(),
    currencyCode: Yup.string().required(),
    costCenterCode: Yup.string().nullable(),
    description: Yup.string().nullable(),
  });

  const valuesToForm = () => {
    const { id, name, bankAccountNumber, iban, currencyCode, costCenterCode, description } = selectedValues;

    setInitValues({
      id,
      name,
      bankAccountNumber,
      iban: iban ?? '',
      currencyCode,
      costCenterCode: costCenterCode ?? '',
      description: description ?? '',
    });
  };

  const handleSubmit = (values, { resetForm }) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      handleInsertObject(values, resetForm);
    }
  };

  const updateObject = async (values) => {
    bankAccountService.updateItem(cleanValues(values)).then((data) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });

      setSelectedValues(data);
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);
    });
  };

  const handleInsertObject = async (values) => {
    bankAccountService
      .createItem(cleanValues(values))
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });

        setSelectedValues(data);
        setRowSelectionModel(data.id);
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
      })
      .finally(() => {
        //@TODO loader
      });
  };

  const handleRemoveElement = async () => {
    bankAccountService.deleteItem(selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues(defaultSelectedValues);
    });
  };

  return (
    <>
      <FormBase
        name="BankAccount"
        leftPanelContent={
          <>
            <div className="h-full overflow-auto custom-scrollbar">
              <FormHeaderUI
                addClick={() => {
                  setSelectedValues(defaultSelectedValues);
                  setViewField(false);
                  setAddField(false);
                }}
                addDisabled={!viewField || !canEdit}
                modClick={() => {
                  setViewField(false);
                  setEditButtonvisible(false);
                }}
                modDisabled={!viewField || operatorsDisabled || !canEdit}
                deleteClick={() => setShowDeleteDialog(true)}
                deleteDisabled={!viewField || operatorsDisabled || !canEdit}
                copyClick={() => {
                  setViewField(false);
                  setAddField(false);
                }}
                copyDisabled={!viewField || operatorsDisabled || !canEdit}
              />

              <Formik
                initialValues={initValues}
                validationSchema={validationSchema}
                validateOnChange={false}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ resetForm }) => (
                  <Form>
                    <BankAccountFields
                      viewField={viewField}
                      autoCompleteOptions={autoCompleteOptions}
                      selectedValues={selectedValues}
                    />

                    <BankAccountTabs disabled={viewField} />

                    <FormButtonPanelUI
                      onCancelClick={() => {
                        setViewField(true);
                        setEditButtonvisible(true);
                        setAddField(true);

                        resetForm();
                      }}
                      disabled={editButtonVisible && addField}
                    />
                  </Form>
                )}
              </Formik>
            </div>
          </>
        }
        rightPanelContent={
          <BankAccountReports
            refetch={{ refetchTable, setRefetchTable }}
            values={{ selectedValues, setSelectedValues }}
            viewField={viewField}
            operatorsDisabled={operatorsDisabled}
            rowSelection={{ rowSelectionModel, setRowSelectionModel }}
            autoCompleteOptions={autoCompleteOptions}
          />
        }
      />
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};
